import React, { useEffect, useMemo, useState } from "react";
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from "./Navigation.module.css";
import { formatFixed } from '../../utils/numberFormat';
import {
  getFinanceCostSeriesForInstance,
  getBudgetCurveMetaForInstance,
  type FinanceCostPoint,
} from "../../Data/Budget/budgetOverrides";
import { getAllProjectsSeedPlusAdded } from "../../Data/ProjectConfig";
import { getProjectKpiConfigOverride } from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import {
  computeCpiHistorySeries,
  getCurrentCpiAsOf,
  getCpiColorBand,
  CPI_TOLERANCE_DEFAULTS,
  type CpiHistoryPoint,
} from "../../kpi-engine/budget";
import PerformanceIndexGauge from "./PerformanceIndexGauge";
import { toISODate } from './thermometerUtils';
import {
  indexColorHex,
  PerformanceIndexHistoryChart,
  VarianceMiniRow,
  VarianceModalOverlay,
  type VarianceHistoryPoint,
} from './varianceChartShared';

// ---------------------------------------------------------------------------
// BudgetVariance — renders a CPI gauge + CPI history chart.
//
// Mirrors ScheduleVariance but for Cost Performance Index (CPI).
// CPI > 1 = under budget = green
// CPI < 1 = over budget = red
// ---------------------------------------------------------------------------

type Props = {
  projectGUID?: string;
  currentUserGuid?: string;
  instanceGuid?: string;
  title?: string;
  displayMode?: "full" | "mini";
  /** Pre-fetched series data from adapter. When provided, skips internal localStorage read. */
  prefetchedSeries?: FinanceCostPoint[];
  /** Pre-fetched CPI thresholds from adapter. When provided, skips internal config read. */
  prefetchedCpiThresholds?: { warnThresh: number; critThresh: number };
  /** Per-instance minimum baseline budget % before CPI activates. Overrides the global default. */
  cpiMinBaselinePct?: number;
  /** When true, renders without the outer scheduleChartWrap border/background (parent provides it). */
  embedded?: boolean;
};

/* --- Main component ------------------------------------------------------ */

export default function BudgetVariance({
  projectGUID,
  instanceGuid,
  title,
  displayMode,
  prefetchedSeries,
  prefetchedCpiThresholds,
  cpiMinBaselinePct,
  embedded,
}: Props) {
  const { t } = useAppTranslation('kpi');
  const [dataVersion, setDataVersion] = useState(0);
  const [fullOpen, setFullOpen] = useState(false);

  useEffect(() => {
    const onChange = () => setDataVersion((v) => v + 1);
    window.addEventListener("l4e_finance_cost_changed" as any, onChange);
    return () =>
      window.removeEventListener(
        "l4e_finance_cost_changed" as any,
        onChange,
      );
  }, []);

  // --- resolve project GUID ---
  const projectGuidNorm = useMemo(() => {
    if (!projectGUID) return "";
    const all = getAllProjectsSeedPlusAdded() || [];
    const g = String(projectGUID).toUpperCase();
    const rec = all.find(
      (p: any) => String(p?.project_GUID || p?.project_guid || "").toUpperCase() === g,
    );
    return String(
      (rec as any)?.project_GUID ||
        (rec as any)?.project_guid ||
        projectGUID ||
        "",
    ).trim();
  }, [projectGUID]);

  const instanceGuidNorm = String(instanceGuid || "").trim();

  // --- load finance cost series ---
  // Prefer pre-fetched data from adapter; fall back to direct localStorage read.
  const series: FinanceCostPoint[] = useMemo(() => {
    if (prefetchedSeries && dataVersion === 0) return prefetchedSeries;
    void dataVersion;
    if (!projectGuidNorm) return [];
    const ov = getFinanceCostSeriesForInstance(
      projectGuidNorm,
      instanceGuidNorm || undefined,
    );
    return ov || [];
  }, [prefetchedSeries, projectGuidNorm, instanceGuidNorm, dataVersion]);

  // --- read CPI thresholds from instance config ---
  // Prefer pre-fetched thresholds from adapter; fall back to direct localStorage read.
  const { warnThresh, critThresh } = useMemo(() => {
    void dataVersion; // intentional recompute on data bump (config read is via stable refs)
    if (prefetchedCpiThresholds) return prefetchedCpiThresholds;
    if (!projectGuidNorm) return { warnThresh: CPI_TOLERANCE_DEFAULTS.warning_threshold, critThresh: CPI_TOLERANCE_DEFAULTS.critical_threshold };
    try {
      const ov = getProjectKpiConfigOverride(projectGuidNorm as any);
      const cats = (ov as any)?.category_instances || {};
      const finArr: any[] = (cats as any)?.finance || [];
      const inst = finArr.find(
        (x: any) =>
          String(x?.instance_guid || "") === instanceGuidNorm ||
          String(x?.catalog_kpi_guid || "").toLowerCase() === "kpi_cost_variance_v1",
      );
      return {
        warnThresh:
          inst?.cpi_warning_threshold ?? CPI_TOLERANCE_DEFAULTS.warning_threshold,
        critThresh:
          inst?.cpi_critical_threshold ?? CPI_TOLERANCE_DEFAULTS.critical_threshold,
      };
    } catch {
      return { warnThresh: CPI_TOLERANCE_DEFAULTS.warning_threshold, critThresh: CPI_TOLERANCE_DEFAULTS.critical_threshold };
    }
  }, [prefetchedCpiThresholds, projectGuidNorm, instanceGuidNorm, dataVersion]);

  // --- compute CPI series (current + past only, no future) ---
  // Effective early-maturity guard: per-instance override > global default.
  const effectiveGuardPct = (typeof cpiMinBaselinePct === "number" && Number.isFinite(cpiMinBaselinePct))
    ? cpiMinBaselinePct
    : CPI_TOLERANCE_DEFAULTS.early_maturity_guard_pct;

  const { cpiHistory, cpiDiag } = useMemo(() => {
    const diag = {
      seriesLength: series.length,
      pastDateCount: 0,
      futureDateCount: 0,
      belowGuardCount: 0,
      nullActualCount: 0,
      maxBaseline: 0,
      effectiveGuardPct,
      firstDate: "",
      lastDate: "",
      today: "",
    };
    if (!series.length) return { cpiHistory: [] as CpiHistoryPoint[], cpiDiag: diag };
    const today = toISODate(new Date());
    diag.today = today;
    if (series.length) {
      const sorted = [...series].sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));
      diag.firstDate = sorted[0]?.reporting_date || "";
      diag.lastDate = sorted[sorted.length - 1]?.reporting_date || "";
    }
    const pastRows = series.filter((r) => r.reporting_date <= today);
    diag.pastDateCount = pastRows.length;
    diag.futureDateCount = series.length - pastRows.length;
    diag.nullActualCount = pastRows.filter((r) => r.actual_spend === null || r.actual_spend === undefined).length;
    const mapped = pastRows
      .map((r) => ({
        project_GUID: projectGuidNorm,
        reporting_date: r.reporting_date,
        baseline_budget: Number(r.baseline_budget) || 0,
        actual_spend: Number(r.actual_spend) || 0,
      }));
    diag.belowGuardCount = mapped.filter((r) => r.baseline_budget < effectiveGuardPct).length;
    diag.maxBaseline = mapped.reduce((mx, r) => Math.max(mx, r.baseline_budget), 0);
    const history = computeCpiHistorySeries(mapped, {
      warning_threshold: warnThresh,
      critical_threshold: critThresh,
      early_maturity_guard_pct: effectiveGuardPct,
    });
    // Diagnostic console output to trace CPI data-flow issues.
    if (!history.length && series.length) {
      console.warn("[BudgetVariance] CPI history is empty despite having series data.", diag);
    }
    return { cpiHistory: history, cpiDiag: diag };
  }, [series, warnThresh, critThresh, projectGuidNorm, effectiveGuardPct]);

  const todayISO = toISODate(new Date());

  // Build a human-readable reason when CPI has no data despite series existing.
  const cpiEmptyReason = useMemo(() => {
    if (cpiHistory.length > 0) return "";
    if (!series.length) return "No cost data available. Open Manage Budget to generate a baseline.";
    const d = cpiDiag;
    if (d.pastDateCount === 0 && d.futureDateCount > 0) {
      return `All ${d.futureDateCount} data point(s) are after today (${d.today}). Earliest date: ${d.firstDate}. Re-open Manage Budget and Apply with the current project date range.`;
    }
    if (d.pastDateCount > 0 && d.belowGuardCount === d.pastDateCount) {
      return `All ${d.pastDateCount} past date(s) have baseline budget < ${d.effectiveGuardPct}% (early-maturity guard). Max baseline: ${formatFixed(d.maxBaseline, 1)}%. CPI will appear as the project matures, or lower the threshold in KPI Configuration.`;
    }
    if (d.pastDateCount > 0) {
      return `${d.pastDateCount} past date(s) found but CPI could not be computed. Check that baseline budget values are populated.`;
    }
    return "";
  }, [cpiHistory, series, cpiDiag]);

  const currentCpi = useMemo(
    () => getCurrentCpiAsOf(cpiHistory, todayISO),
    [cpiHistory, todayISO],
  );
  const previousCpi = useMemo(() => {
    if (!currentCpi || cpiHistory.length < 2) return null;
    const earlier = cpiHistory.filter((p) => p.date < currentCpi.date);
    return earlier.length ? earlier[earlier.length - 1] : null;
  }, [cpiHistory, currentCpi]);

  const band = currentCpi ? getCpiColorBand(currentCpi.cpi, { warning_threshold: warnThresh, critical_threshold: critThresh }) : 0;
  const bandColorHex = indexColorHex(band);
  // CPI > 1 = under budget (good), CPI < 1 = over budget (bad), ≈1 = on budget
  const bandLabel =
    !currentCpi ? "—"
    : currentCpi.cpi >= 1.0 ? "Under"
    : currentCpi.cpi >= 0.95 ? "On"
    : "Over";

  // --- Normalize CPI history to generic VarianceHistoryPoint ---
  const chartData: VarianceHistoryPoint[] = useMemo(
    () => cpiHistory.map((p) => ({ date: p.date, value: p.cpi, belowGuard: p.belowGuard })),
    [cpiHistory],
  );

  // --- Date range for x-axis alignment ---
  const fullDateRangeMin = useMemo(
    () => series.length ? series.reduce((a, b) => a.reporting_date < b.reporting_date ? a : b).reporting_date : undefined,
    [series],
  );
  const fullDateRangeMax = useMemo(
    () => series.length ? series.reduce((a, b) => a.reporting_date > b.reporting_date ? a : b).reporting_date : undefined,
    [series],
  );

  const displayTitle = title || "Cost Variance (CPI)";

  // --- mini mode ---
  const wantsMini = displayMode === "mini";
  const showAsModal = wantsMini && fullOpen;

  if (wantsMini && !fullOpen) {
    return (
      <VarianceMiniRow
        title={displayTitle}
        currentValue={currentCpi?.cpi ?? null}
        bandColorHex={bandColorHex}
        bandLabel={bandLabel}
        onExpand={() => setFullOpen(true)}
      />
    );
  }

  // --- full mode inner content (header + body, no outer wrap) ---
  const innerContent = (
    <>
      {/* Header — uses same CSS class as Progress */}
      <div className={styles.scheduleChartHeader}>
        <div className={styles.scheduleChartTitle}>
          {displayTitle}
        </div>
      </div>

      {/* Body: gauge + chart — uses same CSS class as Progress */}
      <div className={styles.scheduleChartBody}>
        {/* Left: Gauge — uses same CSS class as Progress side panel */}
        <div className={styles.scheduleSidePanel} style={{ padding: "6px 4px 4px", flex: "0 0 220px" }}>
          <PerformanceIndexGauge
            currentValue={currentCpi?.cpi ?? 0}
            previousValue={previousCpi?.cpi ?? null}
            warningThreshold={warnThresh}
            criticalThreshold={critThresh}
            metricLabel={undefined}
            primaryLabel="Actual Spend"
            secondaryLabel="Baseline Budget"
            primaryValue={currentCpi?.actual_spend ?? null}
            secondaryValue={currentCpi?.baseline_budget ?? null}
            lastUpdatedDate={currentCpi?.date ?? null}
            lowLabel="OVER"
            highLabel="UNDER"
          />
        </div>

        {/* Right: CPI History Chart */}
        <div className={styles.scheduleChartPanel}>
          <PerformanceIndexHistoryChart
            data={chartData}
            warningThreshold={warnThresh}
            criticalThreshold={critThresh}
            asOfDate={todayISO}
            asOfColor={bandColorHex}
            fullDateRangeMin={fullDateRangeMin}
            fullDateRangeMax={fullDateRangeMax}
            emptyReason={cpiEmptyReason}
            watermarkHigh="UNDER"
            watermarkLow="OVER"
            tooltipLabel="CPI"
          />
        </div>
      </div>
    </>
  );

  // When embedded inside a parent container (combined Progress + CPI box),
  // skip the outer scheduleChartWrap — the parent already provides it.
  const fullContent = embedded ? innerContent : (
    <div className={styles.scheduleChartWrap}>{innerContent}</div>
  );

  // --- modal overlay (when expanded from mini) ---
  if (showAsModal) {
    return (
      <VarianceModalOverlay
        title={displayTitle}
        fullContent={fullContent}
        onCollapse={() => setFullOpen(false)}
      />
    );
  }

  return fullContent;
}
